/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { type FC, useEffect, useState } from 'react'
import { type RouteComponentProps, Link } from '@reach/router'
import { t } from 'i18next'
import { format } from 'date-fns'
import { isEmpty, uniqBy, capitalize } from 'lodash'
import {
  Dropdown,
  Icon,
  Input,
  Select,
  SelectValue,
  Table,
  TableBody,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  Button,
  Badge,
  Tooltip,
  SkeletonLoader,
  Pagination
} from '@awell-health/design-system'

import { usePathwayContext } from '../../../hooks/usePathwayContext'
import {
  useGetPathwayWebhookCalls,
  type WebhookCall,
  type WebhookCallResponse
} from '../../../hooks/useGetPathwayWebhookCalls'
import { useRetryAllFailedWebhookCalls } from '../../../hooks/useRetryAllFailedWebhookCalls'
import { useRetryAllWebhookCalls } from '../../../hooks/useRetryAllWebhookCalls'
import { useRetryWebhookCall } from '../../../hooks/useRetryWebhookCall'

import type { WebhookEventType, WebhookStatus } from '../types'

interface WebhookFilters {
  statusCodes: Array<string>
  query: string
}

export const WebhooksCallList: FC<RouteComponentProps> = () => {
  const { pathwayId } = usePathwayContext()
  const { retryWebhook } = useRetryWebhookCall()

  const { loading, webhookCalls } = useGetPathwayWebhookCalls(pathwayId!)
  const { retryFailedWebhookCalls } = useRetryAllFailedWebhookCalls(pathwayId!)
  const { retryAllWebhookCalls } = useRetryAllWebhookCalls(pathwayId!)
  const [appliedFilters, setFilters] = useState<WebhookFilters>({
    query: '',
    statusCodes: []
  })
  const [rows, setRows] = useState<Array<WebhookCall>>([])
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  useEffect(() => {
    if (!loading && !!Array.isArray(webhookCalls)) {
      setRows(webhookCalls)
      setCurrentPage(1) // Reset to first page when data changes
    }
  }, [loading, webhookCalls?.length])

  // Calculate pagination
  const totalCount = rows.length
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedRows = rows.slice(startIndex, endIndex)

  const getStatusBadgeVariant = (status: number): string => {
    if (status >= 100 && status <= 199) {
      return 'primary'
    }
    if (status >= 200 && status <= 299) {
      return 'success'
    }
    if (status >= 300 && status <= 399) {
      return 'violet'
    }
    if (status >= 400 && status <= 499) {
      return 'error'
    }
    return 'warning'
  }

  const getStatusCodeOptions = (_data: Array<WebhookCall>) => {
    return uniqBy(
      _data
        .map(d => d.responses)
        .flat()
        .map(({ status }) => ({
          label: `${status}`,
          value: `${status}`
        })),
      'value'
    )
  }

  const getWebhookEventTypeKey = (eventType: WebhookEventType) => {
    return `webhook.${eventType}` as const
  }

  const getWebhookStatusKey = (status: WebhookStatus) => {
    return `WEBHOOK_${status}` as const
  }

  const handleSearchByName = (webhook_name: string, query: string): boolean => {
    if (query === '') {
      return true
    }

    return webhook_name.toLowerCase().includes(query.toLowerCase())
  }

  const handleStatusCodeFilter = (responses: Array<WebhookCallResponse>, codes: Array<string>): boolean => {
    if (isEmpty(codes)) {
      return true
    }
    if (isEmpty(responses)) {
      return false
    }
    return codes.includes(`${responses[responses.length - 1].status}`)
  }

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    const newFilters = { ...appliedFilters, query }
    setFilters(newFilters)

    const newRows = (webhookCalls ?? []).filter(({ responses, webhook_name }) => {
      return handleStatusCodeFilter(responses, newFilters.statusCodes) && handleSearchByName(webhook_name, query)
    })

    setRows(newRows)
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handleStatusCodeChange = (selectedOptions: SelectValue) => {
    const statusCodes = Array.isArray(selectedOptions) ? selectedOptions.map(option => option.value) : []
    const newFilters = { ...appliedFilters, statusCodes }
    setFilters(newFilters)

    const newRows = (webhookCalls ?? []).filter(({ responses, webhook_name }) => {
      return handleStatusCodeFilter(responses, statusCodes) && handleSearchByName(webhook_name, newFilters.query)
    })

    setRows(newRows)
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handleRetryAll = async () => {
    await retryAllWebhookCalls()
  }

  const handleRetryFailed = async () => {
    await retryFailedWebhookCalls()
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const columns = [
    { title: t('webhook'), field: 'webhook' },
    { title: t('called_at'), field: 'called_at' },
    { title: t('status'), field: 'status' },
    { title: t('response'), field: 'response' },
    { title: t('trigger_event'), field: 'trigger_event' },
    { title: capitalize(t('action_other')), field: 'action' }
  ]

  return (
    <div>
      <div className="flex items-center justify-between pb-4">
        <div className="flex gap-3 items-center">
          <Input
            placeholder={t('webhook_search_placeholder')}
            prefixIcon={<Icon icon="RiSearchLine" />}
            value={appliedFilters.query}
            onChange={handleQueryChange}
          />
          <Select
            options={getStatusCodeOptions(webhookCalls ?? [])}
            value={appliedFilters.statusCodes.map(code => ({ label: code, value: code }))}
            onChange={handleStatusCodeChange}
            placeholder={t('status')}
            isClearable
            isMulti
          />
        </div>
        <Dropdown
          placement="left"
          buttonLabel={<Icon icon="RiMore2Line" />}
          items={[
            {
              label: t('webhook_retry_all'),
              onClick: handleRetryAll
            },
            {
              label: t('webhook_retry_all_failed'),
              onClick: handleRetryFailed
            }
          ]}
        />
      </div>
      <div>
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map(({ field, title }) => (
                <TableHead key={field} sortable={false}>
                  {title}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading &&
              [...Array(6).keys()].map(i => (
                <TableRow key={i}>
                  {columns.map(({ field }) => (
                    <TableCell key={field}>
                      <SkeletonLoader className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!loading &&
              paginatedRows.map(webhookCall => (
                <TableRow key={webhookCall.id}>
                  <TableCell>
                    <Link to={webhookCall.id}>
                      <div className="text-blue-600 hover:text-blue-800 hover:underline">
                        {webhookCall.webhook_name}
                      </div>
                    </Link>
                  </TableCell>
                  <TableCell className="font-normal">
                    {format(
                      new Date(webhookCall.responses[webhookCall.responses.length - 1].date),
                      'dd MMM yyyy, h:mm:ss a'
                    )}
                  </TableCell>
                  <TableCell className="font-normal">
                    {t(getWebhookStatusKey(webhookCall.status as WebhookStatus))}
                  </TableCell>
                  <TableCell className="font-normal">
                    {webhookCall.status === 'PENDING' || isEmpty(webhookCall.responses) ? (
                      '--'
                    ) : (
                      <Badge
                        variant={getStatusBadgeVariant(webhookCall.responses[webhookCall.responses.length - 1].status)}
                      >
                        {webhookCall.responses[webhookCall.responses.length - 1].status}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-normal">
                    {t(getWebhookEventTypeKey(webhookCall.event_type as WebhookEventType))}
                  </TableCell>
                  <TableCell>
                    <Tooltip datatip="Retry">
                      <Button
                        variant="ghost"
                        size={null}
                        shape="SquareSm"
                        onClick={() => retryWebhook(webhookCall.id, webhookCall.webhook_name)}
                      >
                        <Icon icon="RiRefreshLine" size={20} />
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
          {!loading && totalCount > itemsPerPage && (
            <TableCaption className="py-0">
              <Pagination totalCount={totalCount} onPageChange={handlePageChange} />
            </TableCaption>
          )}
        </Table>
      </div>
    </div>
  )
}

WebhooksCallList.displayName = 'WebhooksCallList'
